import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Paper,
    Chip,
    Avatar,
    Card,
    CardContent,
    CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import { AIResponseHandler } from './AIResponseHandler';
import styles from './AIAssistant.module.css';

interface Message {
    id: string;
    text: string;
    sender: 'user' | 'ai';
    timestamp: Date;
    isLoading?: boolean;
}

interface SuggestedQuestion {
    id: string;
    text: string;
}

const suggestedQuestions: SuggestedQuestion[] = [
    { id: '1', text: "Inventory Status - Get current stock levels and alerts" },
    { id: '2', text: "Sales Performance - View revenue metrics and trends" },
    { id: '3', text: "Business Trends - Analyze market patterns and growth" },
    { id: '4', text: "Sales Forecast - Get predictions based on historical data" },
    { id: '5', text: "Product Analysis - Understand product performance" },
    { id: '6', text: "Supplier Insights - Review supplier relationships" }
];

const AIAssistant: React.FC = () => {
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '0',
            text: "Hello! I'm your AI Assistant. I can help you with:\n• Inventory status\n• Sales performance\n• Business trends\n• Sales forecasts\n• Product analysis\n• Supplier insights\n\nWhat would you like to know?",
            sender: 'ai',
            timestamp: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const messagesEndRef = useRef<null | HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!inputValue.trim() || isProcessing) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            text: inputValue.trim(),
            sender: 'user',
            timestamp: new Date()
        };

        const aiMessage: Message = {
            id: (Date.now() + 1).toString(),
            text: '',
            sender: 'ai',
            timestamp: new Date(),
            isLoading: true
        };

        setMessages(prev => [...prev, userMessage, aiMessage]);
        setInputValue('');
        setIsProcessing(true);

        try {
            // Simulate processing time and get response
            const response = await new Promise<string>(resolve => {
                setTimeout(() => {
                    resolve(AIResponseHandler.generateResponse(userMessage.text));
                }, 1000);
            });

            setMessages(prev => 
                prev.map(msg => 
                    msg.id === aiMessage.id
                        ? { ...msg, text: response, isLoading: false }
                        : msg
                )
            );
        } catch (error) {
            setMessages(prev => 
                prev.map(msg => 
                    msg.id === aiMessage.id
                        ? { ...msg, text: "I apologize, but I encountered an error processing your request. Please try again.", isLoading: false }
                        : msg
                )
            );
        } finally {
            setIsProcessing(false);
        }
    };

    const handleQuestionClick = (question: string) => {
        if (isProcessing) return;
        setInputValue(question);
        handleSend();
    };

    return (
        <Card className={styles.container}>
            <CardContent style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '24px' }}>
                <Box className={styles.header}>
                    <SmartToyIcon className={styles.headerIcon} />
                    <Box>
                        <Typography variant="h6">AI Business Assistant</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Real-time insights at your fingertips
                        </Typography>
                    </Box>
                </Box>

                <Paper className={styles.messageArea} elevation={0}>
                    {messages.map((message) => (
                        <Box
                            key={message.id}
                            className={`${styles.message} ${
                                message.sender === 'ai' ? styles.aiMessage : styles.userMessage
                            }`}
                        >
                            <Avatar className={styles.avatar}>
                                {message.sender === 'ai' ? <SmartToyIcon /> : <PersonIcon />}
                            </Avatar>
                            <Box className={styles.messageContent}>
                                {message.isLoading ? (
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <CircularProgress size={20} />
                                        <Typography variant="body2" color="textSecondary">
                                            Processing your request...
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                                            {message.text}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {message.timestamp.toLocaleTimeString()}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Box>
                    ))}
                    <div ref={messagesEndRef} />
                </Paper>

                <Box className={styles.suggestedQuestions}>
                    {suggestedQuestions.map((question) => (
                        <Chip
                            key={question.id}
                            label={question.text}
                            onClick={() => handleQuestionClick(question.text)}
                            className={styles.questionChip}
                            disabled={isProcessing}
                        />
                    ))}
                </Box>

                <Box className={styles.inputArea}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type your question here..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        disabled={isProcessing}
                        multiline
                        maxRows={3}
                        size="small"
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSend}
                        disabled={!inputValue.trim() || isProcessing}
                        className={styles.sendButton}
                    >
                        <SendIcon />
                    </IconButton>
                </Box>
            </CardContent>
        </Card>
    );
};

export default AIAssistant; 